import InboxOutlined from '@mui/icons-material/InboxOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { useState, type FormEvent } from 'react';
import AppLayout from '../../components/AppLayout/AppLayout';
import { useUser } from '../../context/UserContext';
import { authorTitleShortLabel, isAuthorTitleValue } from '../../utils/authorTitle';
import MultilingualNameMapCell from '../Settings/lists/MultilingualNameMapCell';
import SettingsCrudDialog from '../Settings/lists/SettingsCrudDialog';
import SettingsListDeleteButton from '../Settings/lists/SettingsListDeleteButton';
import SettingsListEditButton from '../Settings/lists/SettingsListEditButton';
import SettingsListSection from '../Settings/lists/SettingsListSection';
import type { AuthorRow } from '../Settings/lists/singleTableListSupport';
import {
  authorNameMapFromRow,
  authorRowSummaryWithTitle,
  formatSettingsRowDate,
  settingsListApiError,
} from '../Settings/lists/singleTableListSupport';
import AuthorForm, {
  authorDraftToApiBody,
  validateAuthorFormDraft,
  type AuthorFormDraft,
} from './AuthorForm';
import { createAuthor, updateAuthor } from './authorsApi';
import { useAuthorsList } from './useAuthorsList';

const cardSx = {
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  p: 2,
  pr: 1,
  pb: 5,
  transition: (theme: Theme) =>
    theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  borderColor: 'divider',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    boxShadow: (theme: Theme) => theme.shadows[3],
  },
};

export default function AuthorList() {
  const { user } = useUser();
  const showAuthorDelete = user?.role !== 'WRITER';
  const { rows, loading, error, load, deleteById, deletingId, bookCountByAuthorId } =
    useAuthorsList<AuthorRow>();
  const [draft, setDraft] = useState<AuthorFormDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setFormError(null);
    setDraft({ mode: 'create', id: '', title: '', name_map: {} });
  };

  const openEdit = (row: AuthorRow) => {
    setFormError(null);
    const rawTitle = (row.title ?? '').trim();
    const t = isAuthorTitleValue(rawTitle) ? rawTitle : '';
    setDraft({
      mode: 'edit',
      id: row.id,
      title: t,
      name_map: authorNameMapFromRow(row),
    });
  };

  const closeForm = () => {
    setDraft(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setFormError(null);
    const validation = validateAuthorFormDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    const body = authorDraftToApiBody(draft);
    setSaving(true);
    try {
      if (draft.mode === 'create') {
        await createAuthor(body);
      } else {
        await updateAuthor({ id: draft.id.trim(), ...body });
      }
      closeForm();
      await load({ silent: true });
    } catch (err: unknown) {
      setFormError(
        settingsListApiError(err, draft.mode === 'create' ? 'Нэмэхэд алдаа гарлаа.' : 'Шинэчлэхэд алдаа гарлаа.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = draft !== null;
  const dialogTitle =
    draft?.mode === 'create' ? 'Шинэ зохиолч' : draft?.mode === 'edit' ? 'Зохиолч өөрчлөх' : '';

  return (
    <AppLayout>
      <Box
        sx={{
          flex: 1,
          py: 4,
          px: { xs: 2, sm: 4 },
          mx: 'auto',
          width: 1,
        }}
      >
        <Box component="header" sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
            Зохиолчид
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Номын зохиолчийн жагсаалт
          </Typography>
        </Box>

        <SettingsCrudDialog
          open={dialogOpen}
          title={dialogTitle}
          error={formError}
          saving={saving}
          onClose={closeForm}
          onSubmit={(e) => void handleSubmit(e)}
        >
          {draft && <AuthorForm draft={draft} onChange={setDraft} />}
        </SettingsCrudDialog>

        <SettingsListSection
          title="Зохиолч"
          subtitle="Олон хэлээр нэр оруулна"
          onAdd={openCreate}
          error={loading ? null : error}
          loading={loading}
          skeletonColumns={4}
        >
          <Box sx={{ p: 2, pt: 1.5 }}>
            {rows.length === 0 ? (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <InboxOutlined sx={{ fontSize: 40, color: 'action.disabled' }} aria-hidden />
                <Typography variant="body2" color="text.secondary">
                  Мэдээлэл байхгүй
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                    lg: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
                  },
                  gap: 2,
                  alignItems: 'stretch',
                }}
              >
                {rows.map((row) => {
                  const titleLine = authorTitleShortLabel(row.title) || undefined;
                  return (
                  <Paper
                    key={row.id}
                    variant="outlined"
                    component="article"
                    aria-label={authorRowSummaryWithTitle(row)}
                    sx={cardSx}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        minWidth: 0,
                        pr: 0.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title={row.id}
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: '1 1 auto',
                        }}
                      >
                        {row.id}
                      </Typography>
                      <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                        <SettingsListEditButton onEdit={() => openEdit(row)} />
                      </Box>
                    </Box>

                    <Box sx={{ flex: '1 1 auto', minWidth: 0, pr: 0.5 }}>
                      {typeof row.name_map === 'string' ? (
                        <Box>
                          {titleLine ? (
                            <Typography
                              variant="caption"
                              color="primary"
                              fontWeight={600}
                              sx={{
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 0.75,
                              }}
                            >
                              {titleLine}
                            </Typography>
                          ) : null}
                          <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                            {String(row.name_map ?? '')}
                          </Typography>
                        </Box>
                      ) : (
                        <MultilingualNameMapCell
                          flat={authorNameMapFromRow(row)}
                          titlePrefix={titleLine}
                          layout="grid"
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        mt: 'auto',
                        pt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        pr: showAuthorDelete ? 4 : 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Шинэчилсэн:{' '}
                        </Box>
                        {(row.updated_by ?? '').trim() || '—'}
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          {' '}
                          ·{' '}
                        </Box>
                        {formatSettingsRowDate(row.updated_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Үүсгэсэн:{' '}
                        </Box>
                        {(row.created_by ?? '').trim() || '—'}
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          {' '}
                          ·{' '}
                        </Box>
                        {formatSettingsRowDate(row.created_at)}
                      </Typography>
                    </Box>

                    {showAuthorDelete ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: (theme) => theme.spacing(0.5),
                          bottom: (theme) => theme.spacing(0.5),
                        }}
                      >
                        <SettingsListDeleteButton
                          id={row.id}
                          deletingId={deletingId}
                          onDelete={deleteById}
                          itemLabel={authorRowSummaryWithTitle(row)}
                          linkedBooksCount={bookCountByAuthorId[row.id] ?? 0}
                        />
                      </Box>
                    ) : null}
                  </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </SettingsListSection>
      </Box>
    </AppLayout>
  );
}
